import { useMemo } from 'react';
import TableContainer from '@mui/material/TableContainer';
import Skeleton from '@mui/material/Skeleton';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useTableSort } from '@hooks';
import { InsightsCard } from './styles';

const stickyHeaderSx = {
    position: 'sticky',
    left: 0,
    zIndex: 3,
    whiteSpace: 'nowrap',
    // Opaque underlay: the theme's translucent header background-color wins the
    // specificity battle against sx, so scrolled column headings would stack with
    // it and show through the sticky cell. backgroundImage is not themed, so it
    // reliably masks whatever scrolls underneath.
    backgroundImage: 'linear-gradient(#f5f5f5, #f5f5f5)',
};

const stickyBodySx = {
    position: 'sticky',
    left: 0,
    zIndex: 1,
    textAlign: 'left',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: 160,
    maxWidth: 220,
    whiteSpace: 'nowrap',
    backgroundColor: '#fff',
    '.MuiTableRow-hover:hover &': {
        backgroundColor: 'var(--action-highlight)',
    },
};

const getCellDisplay = (metrics, type) => {
    if (!metrics) return { text: '—', background: 'transparent' };

    if (type === 'incidents') {
        if (metrics.negativeShare === null) return { text: '—', background: 'transparent' };
        const pct = Math.round(metrics.negativeShare * 100);
        return { text: `${pct}%`, background: `rgba(255, 88, 83, ${metrics.negativeShare})` };
    } else {
        if (metrics.sentimentScore === null) return { text: '—', background: 'transparent' };
        const score = Math.round(metrics.sentimentScore * 100);
        const absVal = Math.abs(metrics.sentimentScore);
        let background;
        if (metrics.sentimentScore > 0) background = `rgba(76, 196, 116, ${absVal})`;
        else if (metrics.sentimentScore < 0) background = `rgba(255, 88, 83, ${absVal})`;
        else background = 'transparent';
        return { text: score > 0 ? `+${score}` : `${score}`, background };
    }
};

const getSortValue = (row, column, type) => {
    const metrics = row.data[column];
    if (!metrics) return null;
    return type === 'incidents' ? metrics.negativeShare : metrics.sentimentScore;
};

const LOCATION_SORT_KEY = 'location';

const isAllLocationsRow = row => row.locationId === null;

const CategoryMatrixTable = ({ type, categories, rows, loading, onCellClick }) => {
    const { t } = useTranslation();

    const sortColumns = useMemo(
        () => ({
            [LOCATION_SORT_KEY]: row => row.locationName,
            ...Object.fromEntries(categories.map(cat => [cat, row => getSortValue(row, cat, type)])),
        }),
        [categories, type],
    );

    const { sort, onSortChange, sortedData } = useTableSort(rows, {
        columns: sortColumns,
        pinnedFirst: isAllLocationsRow,
    });

    const title =
        type === 'incidents'
            ? t('AnalyticsOperationalInsights.incidents')
            : t('AnalyticsOperationalInsights.sentimentScore');
    const subtitle =
        type === 'incidents'
            ? t('AnalyticsOperationalInsights.incidentsSubtitle')
            : t('AnalyticsOperationalInsights.sentimentScoreSubtitle');

    const columnNames = [
        {
            key: LOCATION_SORT_KEY,
            sortKey: LOCATION_SORT_KEY,
            sx: stickyHeaderSx,
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.location')}
                </Typography>
            ),
        },
        ...categories.map(cat => ({
            key: cat,
            sortKey: cat,
            sx: { whiteSpace: 'nowrap' },
            component: <Typography variant="subtitle2">{t(`themes.${cat}`, cat)}</Typography>,
        })),
    ];

    const tableRows = sortedData.map(row => {
        const isPinned = isAllLocationsRow(row);
        const locationLabel = isPinned ? t('common.allLocations') : row.locationName;
        return [
            {
                value: locationLabel,
                title: locationLabel,
                sx: stickyBodySx,
                component: <Typography variant={isPinned ? 'subtitle2' : 'body2'}>{locationLabel}</Typography>,
            },
            ...categories.map(cat => {
                const { text, background } = getCellDisplay(row.data[cat], type);
                const clickable = text !== '—';
                return {
                    sx: {
                        backgroundColor: background,
                        cursor: clickable ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                    },
                    onClick: clickable ? () => onCellClick(row.locationId, cat) : undefined,
                    component: (
                        <Typography variant={isPinned ? 'subtitle2' : 'body2'} align="center">
                            {text}
                        </Typography>
                    ),
                };
            }),
        ];
    });

    return (
        <InsightsCard>
            <div style={{ marginBottom: 16 }}>
                <Typography variant="title1" color="textPrimary" style={{ display: 'block' }}>
                    {title}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
                    {subtitle}
                </Typography>
            </div>
            {loading ? (
                <Skeleton variant="rectangular" width="100%" height={118} />
            ) : (
                <TableContainer>
                    <Table
                        withHover
                        data={sortedData}
                        columnNames={columnNames}
                        rows={tableRows}
                        sort={sort}
                        onSortChange={onSortChange}
                    />
                </TableContainer>
            )}
        </InsightsCard>
    );
};

export default CategoryMatrixTable;
